import { c as _c } from "react/compiler-runtime";
import { formatCents, parseCents } from 'betterplace-utils';
import classNames from 'classnames';
import { AmountField, Button, Icon, Modal, Keys } from 'design-system/client';
import level0 from 'assets/images/donations/codonation/slider/level-0.png';
import level1 from 'assets/images/donations/codonation/slider/level-1.png';
import level2 from 'assets/images/donations/codonation/slider/level-2.png';
import level3 from 'assets/images/donations/codonation/slider/level-3.png';
import level4 from 'assets/images/donations/codonation/slider/level-4.png';
import level5 from 'assets/images/donations/codonation/slider/level-5.png';
import level6 from 'assets/images/donations/codonation/slider/level-6.png';
import level7 from 'assets/images/donations/codonation/slider/level-7.png';
import level8 from 'assets/images/donations/codonation/slider/level-8.png';
import { RangeInput } from 'design-system/server';
import Image, { type StaticImageData } from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import styles from './CodonationSelection.module.css';
import type { ModalContentProps } from './types';
import CodonationCalculator from '@/donationForm/_dependencies/CodonationCalculator';
import ZeroCodonationMessage, { ZeroCodonationMessageContextProvider } from '@/donationPages/_dependencies/components/formParts/ZeroCodonationMessage';
import { useCodonationValue, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import useCodonationModalState from '@/donationPages/_dependencies/helpers/useCodonationModalState';
function CodonationFlowers({
  value,
  maxAmount
}: {
  value: number;
  maxAmount: number;
}) {
  const [previousImageIndex, setPreviousImageIndex] = useState<number | null>(null);
  const [shouldDelayTransition, setShouldDelayTransition] = useState(false);
  const currentPercentage = useMemo(() => value / maxAmount * 100, [value, maxAmount]);
  const images: {
    showImage: boolean;
    src: StaticImageData | string;
  }[] = [{
    showImage: 0 <= currentPercentage && currentPercentage < 3,
    src: level0
  }, {
    showImage: 3 <= currentPercentage && currentPercentage < 8,
    src: level1
  }, {
    showImage: 8 <= currentPercentage && currentPercentage < 14,
    src: level2
  }, {
    showImage: 14 <= currentPercentage && currentPercentage < 22,
    src: level3
  }, {
    showImage: 22 <= currentPercentage && currentPercentage < 34,
    src: level4
  }, {
    showImage: 34 <= currentPercentage && currentPercentage < 48,
    src: level5
  }, {
    showImage: 48 <= currentPercentage && currentPercentage < 66,
    src: level6
  }, {
    showImage: 66 <= currentPercentage && currentPercentage < 88,
    src: level7
  }, {
    showImage: 88 <= currentPercentage,
    src: level8
  }];
  const currentImageIndex = images.findLastIndex(image => image.showImage);
  if (currentImageIndex !== previousImageIndex) {
    if (previousImageIndex !== null && previousImageIndex < currentImageIndex) {
      setShouldDelayTransition(true);
    } else if (previousImageIndex !== null && previousImageIndex > currentImageIndex) {
      setShouldDelayTransition(false);
    }
    setPreviousImageIndex(currentImageIndex);
  }
  return <>
      {images.map((image_0, index) => <Image className={classNames(styles.image, {
      [styles.visibleImage!]: image_0.showImage,
      [styles.delayTransition!]: shouldDelayTransition,
      [styles.previousImage!]: previousImageIndex !== null && previousImageIndex < index
    })} src={image_0.src} key={index} loading="eager" alt="" fill />)}
    </>;
}
function ModalContent(t0) {
  const $ = _c(60);
  const {
    handleClose
  } = t0;
  const t = useTranslations();
  const locale = useLocale();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["amount_cents"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [amountCents] = useDonationFormValues(t1);
  const maxAmount = CodonationCalculator.max(amountCents) / 100;
  const [value, setValue, commitValue] = useCodonationValue();
  let t2;
  if ($[1] !== commitValue || $[2] !== handleClose) {
    t2 = () => {
      commitValue();
      handleClose();
    };
    $[1] = commitValue;
    $[2] = handleClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const storeAndClose = t2;
  let t3;
  if ($[4] !== storeAndClose) {
    t3 = event => {
      if (event.key !== Keys.Enter) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      storeAndClose();
    };
    $[4] = storeAndClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleEnter = t3;
  let t4;
  if ($[6] !== setValue) {
    t4 = t5 => {
      const {
        target
      } = t5;
      setValue(parseCents(target.value));
    };
    $[6] = setValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onValueChanged = t4;
  let t5;
  if ($[8] !== t) {
    t5 = t("nextjs.donate.codonation_box.modal_text");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <p>{t5}</p>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = maxAmount * 100;
  let t8;
  if ($[12] !== t7 || $[13] !== value) {
    t8 = <div className={styles.illustration}><CodonationFlowers value={value} maxAmount={t7} /></div>;
    $[12] = t7;
    $[13] = value;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = value / 100;
  let t10;
  if ($[15] !== t) {
    t10 = t("nextjs.donate.attributes.codonation");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== maxAmount || $[18] !== onValueChanged || $[19] !== t10 || $[20] !== t9) {
    t11 = <div className={styles.slider}><RangeInput onChange={onValueChanged} value={t9} max={maxAmount} min={0} aria-label={t10} /></div>;
    $[17] = maxAmount;
    $[18] = onValueChanged;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("nextjs.donate.attributes.codonation");
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = value / 100;
  const t14 = amountCents / 100;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Icon name="euroSign" title="Euro" color="fg-content-secondary" id="codonation_cents-suffix" />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== handleEnter || $[26] !== locale || $[27] !== onValueChanged || $[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t16 = <div className={styles.inputFlexContainer}><div className={styles.inputWrapper}><AmountField label={t12} id="codonation-input" name="codonation_cents" value={t13} onChange={onValueChanged} onBlur={onValueChanged} onKeyDown={handleEnter} inputSize="large" step="1" max={t14} invisibleLabel={true} hideSteps={true} locale={locale}>{t15}</AmountField></div></div>;
    $[25] = handleEnter;
    $[26] = locale;
    $[27] = onValueChanged;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div> = </div>;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t) {
    t18 = t("nextjs.donate.codonation_box.sum_label");
    $[33] = t;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = Math.round(value) + amountCents;
  let t20;
  if ($[35] !== locale || $[36] !== t19) {
    t20 = formatCents(locale, t19, {
      precision: 2
    });
    $[35] = locale;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t18 || $[39] !== t20) {
    t21 = <div className={styles.sum}>{t18} {t20}</div>;
    $[38] = t18;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t16 || $[42] !== t21) {
    t22 = <div className={styles.inputAndSum}>{t16}{t17}{t21}</div>;
    $[41] = t16;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== value) {
    t23 = <ZeroCodonationMessage value={value} className={styles.zeroCodonationMessage} />;
    $[44] = value;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t) {
    t24 = t("nextjs.donate.codonation_box.apply_button");
    $[46] = t;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== storeAndClose || $[49] !== t24) {
    t25 = <Button kind="primary" type="button" onClick={storeAndClose} className={styles.closeCodonationModalButton}>{t24}</Button>;
    $[48] = storeAndClose;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t11 || $[52] !== t22 || $[53] !== t23 || $[54] !== t25 || $[55] !== t8) {
    t26 = <div className={styles.codonationFormWrapper}>{t8}{t11}{t22}{t23}{t25}</div>;
    $[51] = t11;
    $[52] = t22;
    $[53] = t23;
    $[54] = t25;
    $[55] = t8;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t26 || $[58] !== t6) {
    t27 = <div>{t6}{t26}</div>;
    $[57] = t26;
    $[58] = t6;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  return t27;
}
function CodonationSelection() {
  const $ = _c(28);
  const t = useTranslations("nextjs");
  const locale = useLocale();
  const {
    modalShown,
    openModal,
    hideModal,
    codonationCents
  } = useCodonationModalState();
  let t0;
  if ($[0] !== codonationCents || $[1] !== locale) {
    t0 = formatCents(locale, codonationCents);
    $[0] = codonationCents;
    $[1] = locale;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = `+ ${t0}`;
  let t2;
  if ($[3] !== t1) {
    t2 = <div className={styles.codonationAddition}>{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("donate.codonation_box.trigger_button");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== openModal || $[8] !== t3) {
    t4 = <Button onClick={openModal} kind="secondary" type="button">{t3}</Button>;
    $[7] = openModal;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className={styles.modalTriggerContainer}>{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("donate.codonation_box.modal_headline");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("core.close");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== hideModal) {
    t8 = <ModalContent handleClose={hideModal} />;
    $[17] = hideModal;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== hideModal || $[20] !== modalShown || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <ZeroCodonationMessageContextProvider><Modal id="codonation-selection-modal" onClose={hideModal} isOpen={modalShown} title={t6} closeText={t7} hideActions={true} fullWidth={true} className={styles.modal}>{t8}</Modal></ZeroCodonationMessageContextProvider>;
    $[19] = hideModal;
    $[20] = modalShown;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t5 || $[26] !== t9) {
    t10 = <div>{t5}{t9}</div>;
    $[25] = t5;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
export default CodonationSelection;